import React from 'react';

import dayjs from 'dayjs';
import cn from 'classnames';

import { StabilityChart } from './StabilityChart';
import { Recommendation } from './Recommendation';
import { CalendarIcon, ClockIcon } from 'assets/icons';
import { PatientMedicalRecord } from 'record/patient-medical.record';
import { AssessmentResultType, AssessmentType, MissingAssessmentType } from 'types/assessment';

type AssessmentPdfContentProps = {
  targetRef: React.RefObject<HTMLDivElement>;
  patientMedicalData: PatientMedicalRecord;
  isMissingAssessment: boolean;
  currentResult: AssessmentResultType;
  currentAssessmentIndex: number;
  assessmentResults: (AssessmentResultType | MissingAssessmentType)[];
  determineDirection: string;
  STABILITY_INDICATORS: any[];
  isSymptomScoreNotBelowThreshold: (id: string, score: number) => boolean;
  isSymptomChecked: (id: string) => boolean;
  TREATMENT_RECOMMENDATIONS: any[];
  fetchPatientAssessmentResults: () => void;
  assessment: AssessmentType;
  COMFORT_RECOMMENDATIONS: any[];
  qrCodeSvg: string | null;
};

export function AssessmentPdfContent({
  targetRef,
  patientMedicalData,
  isMissingAssessment,
  currentResult,
  currentAssessmentIndex,
  assessmentResults,
  determineDirection,
  STABILITY_INDICATORS,
  isSymptomScoreNotBelowThreshold,
  isSymptomChecked,
  TREATMENT_RECOMMENDATIONS,
  fetchPatientAssessmentResults,
  assessment,
  COMFORT_RECOMMENDATIONS,
  qrCodeSvg,
}: AssessmentPdfContentProps) {
  return (
    <div ref={targetRef} className="py-12 px-12 flex flex-col gap-6 fixed -z-50 min-w-[1024px]">
      <div className="pdf-patient-id flex pt-4 items-center gap-3">
        <div className="flex flex-col">
          <h2 className="text-gray-900  text-[28px] font-semibold leading-[normal]">
            ID: {patientMedicalData?.ref_id}
          </h2>
          <p className="text-gray-500  text-sm font-medium leading-[140%]">{patientMedicalData?.getAge()}</p>
        </div>
        <div className="flex items-end ml-auto">
          <div>
            <p className="text-gray-500  text-sm font-normal leading-[130%]">Assessment:</p>
            {isMissingAssessment ? (
              <p className="relative flex pt-1 items-center gap-1.5 text-gray-900  text-[19px] font-semibold leading-7 ">
                <CalendarIcon />
                <span className="">Unknown Date</span>
              </p>
            ) : (
              <div className="flex pt-1 gap-6">
                <p className="relative flex items-center gap-1.5 text-gray-900  text-[19px] font-semibold leading-7 ">
                  <span className="absolute top-1 -right-[12px] w-[1px] h-[20px] bg-mirage/30" />
                  <CalendarIcon />
                  <span className="">{dayjs(currentResult.created).format('MM/DD/YYYY')}</span>
                </p>
                <p className="flex items-center gap-1.5 text-gray-900  text-[19px] font-semibold leading-7">
                  <ClockIcon />
                  <span className="">{dayjs(currentResult.created).format('HH:mm')}</span>
                </p>
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          dangerouslySetInnerHTML={{ __html: qrCodeSvg || '' }}
          className="w-44 h-44 ml-auto bg-demo-qr  bg-center bg-no-repeat border border-gray-200  bg-white-400  p-2 rounded-xl border-solid"
        />
      </div>

      <div className="pdf-monitor-stability flex flex-col gap-4 bg-white-400 border pt-6 px-6 pb-[22px] rounded-2xl">
        <StabilityChart
          ignoreDarkMode={true}
          currentLevelIndex={currentAssessmentIndex}
          stabilityLevels={assessmentResults.map(result => result.overall_score)}
          levelDirection={determineDirection}
          onSelectResult={() => void 0}
        />
        <h2 className={cn('-mt-6 text-gray-900  text-base font-semibold leading-7 pb-3')}>
          Clinical Stability Indicators (CSI)
        </h2>
        <div className="flex">
          {STABILITY_INDICATORS.map((indicator: any, index: number) => (
            <div
              key={index}
              className="flex flex-col items-center gap-2.5 w-1/6 my-[11.5px] border-r-[1px] last:border-none border-white-300"
            >
              <indicator.icon
                className={
                  isSymptomScoreNotBelowThreshold(indicator.symptomId, indicator.checkingScore) ||
                  isSymptomChecked(indicator.symptomId)
                    ? indicator.unstableClass
                    : indicator.stableClass
                }
              />
              <p className="text-gray-900 text-center text-xs font-medium leading-[17px]">{indicator.title}</p>
            </div>
          ))}
        </div>
      </div>
      {!isMissingAssessment ? (
        <div className="pdf-treatment-recommendations flex flex-col items-start gap-4 self-stretch bg-white-400  p-6 border rounded-2xl">
          <h2 className="text-gray-900  text-base font-semibold leading-7">Treatment Recommendations</h2>
          <div className="flex justify-between gap-6 w-full">
            {TREATMENT_RECOMMENDATIONS.map(recommendation => (
              <Recommendation
                ignoreDarkMode={true}
                key={recommendation.id}
                isComfortMeasures={false}
                name={recommendation.name}
                id={recommendation.id}
                currentResult={currentResult}
                afterUpdate={fetchPatientAssessmentResults}
                comfortRecommendations={assessment?.comfort_recommendations ?? []}
                isShowControls={false}
              />
            ))}
          </div>
        </div>
      ) : null}
      {!isMissingAssessment ? (
        <div className="pdf-comfort-recommendations flex mb-6 flex-col items-start gap-4 self-stretch bg-white-400  p-6 border rounded-2xl">
          <h2 className="text-gray-900  text-base font-semibold leading-7">Comfort recommendations</h2>
          <div className="flex justify-between gap-6 w-full">
            {COMFORT_RECOMMENDATIONS.map(recommendation => (
              <Recommendation
                ignoreDarkMode={true}
                key={recommendation.id}
                isComfortMeasures
                id={recommendation.id}
                name={recommendation.name}
                currentResult={currentResult}
                afterUpdate={fetchPatientAssessmentResults}
                comfortRecommendations={assessment?.comfort_recommendations ?? []}
                isShowControls={false}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
